import React, { useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import NotesPanel, { type Note } from '../../../components/common/NotesPanel';
import ordersService from '../../../services/orders.service';

type Props = {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  /** Create mode: notes the create flow has staged. Passing this switches the
   *  panel to its no-network staging path (NotesPanel's `fetchNotes`/`addNote`
   *  null contract) — nothing is persisted until the order is created, and the
   *  staged text rides the create payload. */
  stagedNotes?: string[];
  /** Create mode: publishes the staged note text back to the flow, so it
   *  survives this tab unmounting on a tab switch. */
  onStagedNotesChange?: (next: string[]) => void;
};

const NotesTab: React.FC<Props> = ({ pronumber, stagedNotes, onStagedNotesChange }) => {
  const { accounts } = useMsal();
  const currentUsername = accounts[0]?.username || accounts[0]?.name || '';
  const staged = stagedNotes != null;

  const fetchNotes = useCallback(() => ordersService.getNotes(pronumber), [pronumber]);
  const addNote = useCallback(
    async (text: string) => {
      await ordersService.addNote(pronumber, text);
    },
    [pronumber]
  );

  // NotesPanel owns the staged list and mirrors it into this ref; the count
  // callback (fired after that mirror effect) is where we lift the text up.
  // Both handles are stable so the panel's notify effect only runs on a real
  // count change — an unstable callback would re-publish on every render.
  const pendingNotesRef = useRef<Note[]>(
    (stagedNotes ?? []).map((text, i) => ({
      recno: -(i + 1),
      datetime: new Date().toISOString(),
      userid: currentUsername || 'you',
      notes: text,
    })),
  );
  const publishRef = useRef(onStagedNotesChange);
  publishRef.current = onStagedNotesChange;
  const handleStagedCountChange = useCallback(() => {
    publishRef.current?.(pendingNotesRef.current.map((n) => n.notes));
  }, []);

  return (
    <div className="px-3 pt-3 pb-6">
      <NotesPanel
        fetchNotes={staged ? null : fetchNotes}
        addNote={staged ? null : addNote}
        pendingNotesRef={staged ? pendingNotesRef : undefined}
        onNoteCountChange={staged ? handleStagedCountChange : undefined}
        currentUser={currentUsername}
      />
    </div>
  );
};

export default NotesTab;
